import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../contexts/AuthContext';
import {
  Plus, Search, Download, Pencil, XCircle, RefreshCw, CheckCircle,
  FileText, X, Printer, Clock, Bell, ChevronDown, ChevronUp, Mail
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';

const STATUS_STYLES = {
  PENDING: 'bg-slate-100 text-slate-600 border-slate-200',
  SENT_TO_MILL: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  CANCELLED: 'bg-red-100 text-red-600 border-red-200',
  // backward compat for old data
  DRAFT: 'bg-slate-100 text-slate-600 border-slate-200',
  FINAL: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  MODIFIED: 'bg-blue-100 text-blue-700 border-blue-200',
};

const STATUS_LABELS = {
  PENDING: 'PENDING',
  SENT_TO_MILL: 'SENT TO MILL',
  CANCELLED: 'CANCELLED',
  DRAFT: 'PENDING',
  FINAL: 'SENT TO MILL',
  MODIFIED: 'SENT TO MILL',
};

function StatusBadge({ status }) {
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold border ${STATUS_STYLES[status] || STATUS_STYLES.PENDING}`}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

function shortenDO(doNum) {
  if (!doNum) return null;
  // New format: IPS--BHL--GNG--0101--0001 (double dashes)
  if (doNum.includes('--')) {
    const parts = doNum.split('--');
    if (parts.length >= 4) {
      // Show GROUP--UNIT--SEQ
      return parts.slice(1).join('--');
    }
    return doNum;
  }
  // Old format: SC-GROUP-YEAR-UNIT-DDMM-SEQ
  const parts = doNum.split('-');
  if (parts.length >= 6) {
    return `${parts[2]}-${parts[3]}-${parts[5]}`;
  }
  return doNum;
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [stats, setStats] = useState({ total: 0, pending: 0, sent: 0, cancelled: 0 });
  const [toast, setToast] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [pdfViewer, setPdfViewer] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  // Collective credit mail selection (Admin/SuperAdmin, SENT_TO_MILL only)
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [collectiveCreditLoading, setCollectiveCreditLoading] = useState(false);
  const [collectiveSaleOrderLoading, setCollectiveSaleOrderLoading] = useState(false);
  const [collectiveUndertakingLoading, setCollectiveUndertakingLoading] = useState(false);

  // Truck edit requests (Admin/SuperAdmin)
  const [pendingTruckEdits, setPendingTruckEdits] = useState([]);
  const [truckEditsPanel, setTruckEditsPanel] = useState(false);

  // Deletion requests
  const [pendingDeletionRequests, setPendingDeletionRequests] = useState([]);
  const [deletionPanel, setDeletionPanel] = useState(false);
  const [deletionModal, setDeletionModal] = useState(null); // { order_id, do_number }
  const [deletionReason, setDeletionReason] = useState('');
  const [deletionSubmitting, setDeletionSubmitting] = useState(false);

  // Sale order edit requests (Admin/SuperAdmin)
  const [pendingSaleOrderEdits, setPendingSaleOrderEdits] = useState([]);
  const [saleOrderEditsPanel, setSaleOrderEditsPanel] = useState(false);

  // Order history modal
  const [historyModal, setHistoryModal] = useState(null);

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), type === 'error' ? 8000 : 4000);
  };

  const PAGE_SIZE = 20;
  const isSuperAdminOrAdmin = ['SUPER_ADMIN', 'ADMIN'].includes(user?.role);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip: page * PAGE_SIZE, limit: PAGE_SIZE });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);
      const res = await api.get(`/orders/?${params}`);
      setOrders(res.data.orders || []);
      setTotal(res.data.total || 0);
    } catch (e) {
      showToast('error', 'Failed to load orders. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, [search, statusFilter, page]);

  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  useEffect(() => {
    async function fetchStats() {
      try {
        const [all, pending, sent, can] = await Promise.all([
          api.get('/orders/?limit=1'),
          api.get('/orders/?limit=1&status=PENDING'),
          api.get('/orders/?limit=1&status=SENT_TO_MILL'),
          api.get('/orders/?limit=1&status=CANCELLED'),
        ]);
        setStats({
          total: all.data.total,
          pending: pending.data.total,
          sent: sent.data.total,
          cancelled: can.data.total,
        });
      } catch (e) {
        // Stats are non-critical — failure is silent to avoid disrupting the main table
      }
    }
    fetchStats();
  }, [orders]);

  const fetchPendingTruckEdits = useCallback(async () => {
    if (!isSuperAdminOrAdmin) return;
    try {
      const res = await api.get('/orders/truck-edit-requests?status=PENDING');
      setPendingTruckEdits(res.data || []);
    } catch (e) {
      showToast('error', 'Could not load truck edit requests');
    }
  }, [isSuperAdminOrAdmin]);

  const fetchPendingSaleOrderEdits = useCallback(async () => {
    if (!isSuperAdminOrAdmin) return;
    try {
      const res = await api.get('/orders/sale-order-requests?status=PENDING');
      setPendingSaleOrderEdits(res.data || []);
    } catch (e) {
      showToast('error', 'Could not load sale order edit requests');
    }
  }, [isSuperAdminOrAdmin]);

  const fetchPendingDeletionRequests = useCallback(async () => {
    if (!isSuperAdminOrAdmin) return;
    try {
      const res = await api.get('/orders/deletion-requests?status=PENDING');
      setPendingDeletionRequests(res.data || []);
    } catch (e) {
      showToast('error', 'Could not load deletion requests');
    }
  }, [isSuperAdminOrAdmin]);

  useEffect(() => {
    fetchPendingTruckEdits();
    fetchPendingSaleOrderEdits();
    fetchPendingDeletionRequests();
  }, [fetchPendingTruckEdits, fetchPendingSaleOrderEdits, fetchPendingDeletionRequests]);

  const handleSubmitDeletionRequest = async () => {
    if (!deletionModal) return;
    setDeletionSubmitting(true);
    try {
      await api.post(`/orders/${deletionModal.order_id}/request-deletion`, { reason: deletionReason });
      setDeletionModal(null);
      setDeletionReason('');
      showToast('success', 'Deletion request submitted. Awaiting Admin approval.');
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Error submitting request');
    } finally { setDeletionSubmitting(false); }
  };

  const handleApproveDeletion = async (orderId, requestId) => {
    try {
      await api.post(`/orders/${orderId}/approve-deletion/${requestId}`);
      fetchPendingDeletionRequests();
      fetchOrders();
    } catch (e) { showToast('error', 'Error approving'); }
  };

  const handleRejectDeletion = async (orderId, requestId) => {
    try {
      await api.post(`/orders/${orderId}/reject-deletion/${requestId}`);
      fetchPendingDeletionRequests();
    } catch (e) { showToast('error', 'Error rejecting'); }
  };

  const handleCreditEmail = async (orderId) => {
    try {
      await api.post(`/orders/${orderId}/send-credit-email`);
      showToast('success', 'Order marked as credit & credit email sent.');
      fetchOrders();
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Error sending credit email');
    }
  };

  const handlePaymentUndertaking = async (orderId) => {
    try {
      await api.post(`/orders/${orderId}/send-payment-undertaking`);
      showToast('success', 'Payment undertaking email sent.');
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Error sending payment undertaking');
    }
  };

  const handleCancel = (orderId) => {
    setConfirm({
      type: 'cancel', orderId,
      title: 'Cancel Order?',
      desc: 'This will mark the order as CANCELLED. This cannot be undone.',
      actionLabel: 'Yes, Cancel',
      actionClass: 'bg-red-600 hover:bg-red-700 text-white',
    });
  };

  const handleSendToMill = (orderId) => {
    setConfirm({
      type: 'finalize', orderId,
      title: 'Send to Mill?',
      desc: 'A permanent DO number will be generated and order status will be updated to Sent to Mill.',
      actionLabel: 'Yes, Send to Mill',
      actionClass: 'bg-green-700 hover:bg-green-800 text-white',
    });
  };

  const executeConfirmed = async () => {
    if (!confirm) return;
    setActionLoading(true);
    try {
      if (confirm.type === 'finalize') {
        const res = await api.post(`/orders/${confirm.orderId}/finalize`);
        showToast('success', 'Order sent to mill! DO number generated.');
        // Surface email config warnings if backend detected issues
        const warns = res.data?._email_warnings;
        if (warns && warns.length > 0) {
          // Show a persistent warning for each email issue
          setTimeout(() => showToast('error', `Email not sent: ${warns[0]}`), 500);
        }
      } else if (confirm.type === 'cancel') {
        await api.post(`/orders/${confirm.orderId}/cancel`);
        showToast('success', 'Order cancelled.');
      }
      fetchOrders();
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Operation failed');
    } finally {
      setActionLoading(false);
      setConfirm(null);
    }
  };

  const handleDownloadPdf = async (orderId, doNumber) => {
    const token = localStorage.getItem('bm_session_token');
    const base = process.env.REACT_APP_BACKEND_URL;
    setPdfLoading(true);
    showToast('success', 'Loading PDF...');
    try {
      const res = await fetch(`${base}/api/orders/${orderId}/pdf?t=${encodeURIComponent(token)}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const name = `DO_${(doNumber || orderId).replace(/\//g, '-')}.pdf`;
      setPdfViewer({ url, name });
      setToast(null);
    } catch (e) {
      showToast('error', 'Failed to load PDF. Please try again.');
    } finally {
      setPdfLoading(false);
    }
  };

  const closePdfViewer = () => {
    if (pdfViewer) {
      URL.revokeObjectURL(pdfViewer.url);
      setPdfViewer(null);
    }
  };

  const handleApproveTruckEdit = async (orderId, requestId) => {
    try {
      await api.post(`/orders/${orderId}/approve-truck-edit/${requestId}`);
      showToast('success', 'Truck edit approved and applied.');
      fetchPendingTruckEdits();
      fetchOrders();
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleRejectTruckEdit = async (orderId, requestId) => {
    try {
      await api.post(`/orders/${orderId}/reject-truck-edit/${requestId}`);
      showToast('success', 'Truck edit request rejected.');
      fetchPendingTruckEdits();
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleApproveSaleOrder = async (orderId, requestId) => {
    try {
      await api.post(`/orders/${orderId}/approve-sale-order/${requestId}`);
      showToast('success', 'Sale Order edit approved.');
      fetchPendingSaleOrderEdits();
      fetchOrders();
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Failed to approve');
    }
  };

  const handleRejectSaleOrder = async (orderId, requestId) => {
    try {
      await api.post(`/orders/${orderId}/reject-sale-order/${requestId}`);
      showToast('success', 'Sale Order edit rejected.');
      fetchPendingSaleOrderEdits();
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Failed to reject');
    }
  };

  const handleViewHistory = async (orderId, doNumber) => {
    setHistoryModal({ orderId, displayId: doNumber || orderId, loading: true, data: null });
    try {
      const res = await api.get(`/orders/${orderId}/history`);
      setHistoryModal(prev => ({ ...prev, loading: false, data: res.data || [] }));
    } catch (e) {
      setHistoryModal(prev => ({ ...prev, loading: false, data: [] }));
      showToast('error', 'Failed to load history');
    }
  };

  const toggleOrderSelection = (orderId) => {
    setSelectedOrders(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const sentIds = orders.filter(o => o.status === 'SENT_TO_MILL').map(o => o.order_id);
    const allSelected = sentIds.length > 0 && sentIds.every(id => selectedOrders.has(id));
    setSelectedOrders(allSelected ? new Set() : new Set(sentIds));
  };

  const handleCollectiveCreditMail = async () => {
    if (!selectedOrders.size) return;
    setCollectiveCreditLoading(true);
    try {
      const res = await api.post('/orders/collective-credit-mail', {
        order_ids: Array.from(selectedOrders),
      });
      const groups = res.data.sent_to_groups || [];
      const warns = res.data.warnings || [];
      showToast('success', `Credit mail queued for: ${groups.join(', ')}${warns.length ? ` (${warns.length} skipped)` : ''}`);
      setSelectedOrders(new Set());
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Failed to send credit mail');
    } finally {
      setCollectiveCreditLoading(false);
    }
  };

  const handleCollectiveSaleOrderMail = async () => {
    if (!selectedOrders.size) return;
    setCollectiveSaleOrderLoading(true);
    try {
      const res = await api.post('/orders/collective-sale-order-mail', {
        order_ids: Array.from(selectedOrders),
      });
      const groups = res.data.sent_to_groups || [];
      const warns = res.data.warnings || [];
      showToast('success', `Sale order mail queued for: ${groups.join(', ')}${warns.length ? ` (${warns.length} skipped)` : ''}`);
      setSelectedOrders(new Set());
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Failed to send sale order mail');
    } finally {
      setCollectiveSaleOrderLoading(false);
    }
  };

  const handleCollectiveUndertakingMail = async () => {
    if (!selectedOrders.size) return;
    setCollectiveUndertakingLoading(true);
    try {
      const res = await api.post('/orders/collective-payment-undertaking-mail', {
        order_ids: Array.from(selectedOrders),
      });
      const groups = res.data.sent_to_groups || [];
      const warns = res.data.warnings || [];
      showToast('success', `Undertaking mail queued for: ${groups.join(', ')}${warns.length ? ` (${warns.length} skipped)` : ''}`);
      setSelectedOrders(new Set());
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Failed to send undertaking mail');
    } finally {
      setCollectiveUndertakingLoading(false);
    }
  };

  const statCards = [
    { label: 'Total Orders', value: stats.total, color: 'text-slate-700', bg: 'bg-white' },
    { label: 'Pending', value: stats.pending, color: 'text-slate-500', bg: 'bg-slate-50' },
    { label: 'Sent to Mill', value: stats.sent, color: 'text-emerald-600', bg: 'bg-emerald-50' },
    { label: 'Cancelled', value: stats.cancelled, color: 'text-red-600', bg: 'bg-red-50' },
  ];

  return (
    <div className="space-y-5" data-testid="dashboard-page">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-sm font-medium
          ${toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}
          data-testid="toast-notification"
        >
          <CheckCircle size={16} />
          {toast.msg}
        </div>
      )}

      {/* Confirm Dialog */}
      <AlertDialog open={!!confirm} onOpenChange={open => !open && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{confirm?.title}</AlertDialogTitle>
            <AlertDialogDescription>{confirm?.desc}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirm(null)} disabled={actionLoading}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={executeConfirmed}
              disabled={actionLoading}
              className={confirm?.actionClass}
              data-testid="confirm-action-btn"
            >
              {actionLoading ? 'Processing...' : confirm?.actionLabel}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 font-['Manrope']">Order Dashboard</h1>
          <p className="text-slate-500 text-xs sm:text-sm mt-0.5">
            {user?.role === 'BROKER' ? 'Showing your assigned orders' : 'All delivery orders'}
          </p>
        </div>
        <Button
          onClick={() => navigate('/orders/new')}
          data-testid="new-order-btn"
          className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
        >
          <Plus size={16} /> <span className="hidden sm:inline">New Order</span><span className="sm:hidden">New</span>
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {statCards.map(card => (
          <div key={card.label} className={`${card.bg} border border-slate-200 rounded-lg p-4`}>
            <div className="text-xs text-slate-500 font-medium uppercase tracking-wider mb-1">{card.label}</div>
            <div className={`text-2xl font-bold ${card.color} font-['Manrope']`}>{card.value}</div>
          </div>
        ))}
      </div>

      {/* Pending Truck Edit Requests — Admin/SuperAdmin only */}
      {isSuperAdminOrAdmin && pendingTruckEdits.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-testid="truck-edit-panel">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell size={15} className="text-amber-600" />
              <span className="text-sm font-semibold text-amber-800">
                {pendingTruckEdits.length} Pending Truck Edit Request{pendingTruckEdits.length > 1 ? 's' : ''}
              </span>
            </div>
            <button
              onClick={() => setTruckEditsPanel(p => !p)}
              className="flex items-center gap-1 text-xs text-amber-700 hover:text-amber-900 font-medium"
              data-testid="truck-edits-toggle"
            >
              {truckEditsPanel ? <><ChevronUp size={13} /> Hide</> : <><ChevronDown size={13} /> View All</>}
            </button>
          </div>
          {truckEditsPanel && (
            <div className="mt-3 space-y-2">
              {pendingTruckEdits.map(req => (
                <div key={req.request_id} className="flex items-center justify-between bg-white border border-amber-100 rounded-md px-3 py-2.5" data-testid={`truck-edit-row-${req.request_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono font-semibold text-slate-700">{shortenDO(req.do_number) || req.order_id}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      <span className="font-medium text-slate-700">{req.requested_by_name}</span> wants to change:{' '}
                      <span className="font-mono text-red-500 line-through">{req.current_truck_no || '—'}</span>
                      {' → '}
                      <span className="font-mono text-green-700 font-semibold">{req.new_truck_no}</span>
                      {req.reason && <span className="text-slate-400 ml-1.5">({req.reason})</span>}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-3 shrink-0">
                    <button
                      onClick={() => handleApproveTruckEdit(req.order_id, req.request_id)}
                      className="px-2.5 py-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100 font-medium"
                      data-testid={`approve-truck-edit-${req.request_id}`}
                    >Approve</button>
                    <button
                      onClick={() => handleRejectTruckEdit(req.order_id, req.request_id)}
                      className="px-2.5 py-1 text-xs bg-red-50 text-red-600 border border-red-200 rounded hover:bg-red-100 font-medium"
                      data-testid={`reject-truck-edit-${req.request_id}`}
                    >Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pending Sale Order Edit Requests — Admin/SuperAdmin only */}
      {isSuperAdminOrAdmin && pendingSaleOrderEdits.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-testid="sale-order-edit-panel">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell size={15} className="text-blue-600" />
              <span className="text-sm font-semibold text-blue-800">
                {pendingSaleOrderEdits.length} Pending Sale Order Edit Request{pendingSaleOrderEdits.length > 1 ? 's' : ''}
              </span>
            </div>
            <button
              onClick={() => setSaleOrderEditsPanel(p => !p)}
              className="flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 font-medium"
              data-testid="sale-order-edits-toggle"
            >
              {saleOrderEditsPanel ? <><ChevronUp size={13} /> Hide</> : <><ChevronDown size={13} /> View All</>}
            </button>
          </div>
          {saleOrderEditsPanel && (
            <div className="mt-3 space-y-2">
              {pendingSaleOrderEdits.map(req => (
                <div key={req.request_id} className="flex items-center justify-between bg-white border border-blue-100 rounded-md px-3 py-2.5" data-testid={`sale-order-edit-row-${req.request_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono font-semibold text-slate-700">{shortenDO(req.do_number) || req.order_id}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      <span className="font-medium text-slate-700">{req.requested_by_name}</span> wants to change Sale Order:{' '}
                      <span className="font-semibold text-red-500">{req.current_value ? 'YES' : 'NO'}</span>
                      {' → '}
                      <span className="font-semibold text-green-700">{req.new_value ? 'YES' : 'NO'}</span>
                      {req.reason && <span className="text-slate-400 ml-1.5">({req.reason})</span>}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-3 shrink-0">
                    <button
                      onClick={() => handleApproveSaleOrder(req.order_id, req.request_id)}
                      className="px-2.5 py-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100 font-medium"
                      data-testid={`approve-sale-order-${req.request_id}`}
                    >Approve</button>
                    <button
                      onClick={() => handleRejectSaleOrder(req.order_id, req.request_id)}
                      className="px-2.5 py-1 text-xs bg-red-50 text-red-600 border border-red-200 rounded hover:bg-red-100 font-medium"
                      data-testid={`reject-sale-order-${req.request_id}`}
                    >Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pending Deletion Requests — Admin/SuperAdmin only */}
      {isSuperAdminOrAdmin && pendingDeletionRequests.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="deletion-requests-panel">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <XCircle size={15} className="text-red-600" />
              <span className="text-sm font-semibold text-red-800">
                {pendingDeletionRequests.length} Pending Deletion Request{pendingDeletionRequests.length > 1 ? 's' : ''}
              </span>
            </div>
            <button
              onClick={() => setDeletionPanel(p => !p)}
              className="flex items-center gap-1 text-xs text-red-700 hover:text-red-900 font-medium"
              data-testid="deletion-panel-toggle"
            >
              {deletionPanel ? <><ChevronUp size={13} /> Hide</> : <><ChevronDown size={13} /> View All</>}
            </button>
          </div>
          {deletionPanel && (
            <div className="mt-3 space-y-2">
              {pendingDeletionRequests.map(req => (
                <div key={req.request_id} className="flex items-center justify-between bg-white border border-red-100 rounded-md px-3 py-2.5" data-testid={`deletion-req-row-${req.request_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-mono font-semibold text-slate-700">{shortenDO(req.do_number) || req.order_id}</div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      <span className="font-medium text-slate-700">{req.requested_by_name}</span> requested order deletion
                      {req.reason && <span className="text-slate-400 ml-1.5">— "{req.reason}"</span>}
                    </div>
                  </div>
                  <div className="flex gap-2 ml-3 shrink-0">
                    <button
                      onClick={() => handleApproveDeletion(req.order_id, req.request_id)}
                      className="px-2.5 py-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100 font-medium"
                      data-testid={`approve-deletion-${req.request_id}`}
                    >Approve</button>
                    <button
                      onClick={() => handleRejectDeletion(req.order_id, req.request_id)}
                      className="px-2.5 py-1 text-xs bg-red-50 text-red-600 border border-red-200 rounded hover:bg-red-100 font-medium"
                      data-testid={`reject-deletion-${req.request_id}`}
                    >Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-lg p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              placeholder="Search DO #, Party, Truck..."
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              className="pl-9 text-sm"
              data-testid="order-search-input"
            />
          </div>
          <select
            value={statusFilter}
            onChange={e => { setStatusFilter(e.target.value); setPage(0); }}
            className="border border-slate-300 rounded-md px-3 py-2 text-sm text-slate-700 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="status-filter"
          >
            <option value="">All Statuses</option>
            <option value="PENDING">Pending</option>
            <option value="SENT_TO_MILL">Sent to Mill</option>
            <option value="CANCELLED">Cancelled</option>
          </select>
          <Button variant="outline" size="icon" onClick={fetchOrders} className="shrink-0">
            <RefreshCw size={15} />
          </Button>
          {/* Batch mail buttons — desktop inline */}
          {isSuperAdminOrAdmin && selectedOrders.size > 0 && (
            <Button
              onClick={handleCollectiveCreditMail}
              disabled={collectiveCreditLoading}
              className="hidden sm:flex shrink-0 bg-purple-600 hover:bg-purple-700 text-white gap-2 text-xs"
              data-testid="collective-credit-mail-btn"
            >
              <Mail size={14} />
              {collectiveCreditLoading ? 'Sending...' : `Credit Mail (${selectedOrders.size})`}
            </Button>
          )}
          {isSuperAdminOrAdmin && selectedOrders.size > 0 && (
            <Button
              onClick={handleCollectiveSaleOrderMail}
              disabled={collectiveSaleOrderLoading}
              className="hidden sm:flex shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white gap-2 text-xs"
              data-testid="collective-sale-order-mail-btn"
            >
              <Mail size={14} />
              {collectiveSaleOrderLoading ? 'Sending...' : `Sale Mail (${selectedOrders.size})`}
            </Button>
          )}
          {isSuperAdminOrAdmin && selectedOrders.size > 0 && (
            <Button
              onClick={handleCollectiveUndertakingMail}
              disabled={collectiveUndertakingLoading}
              className="hidden sm:flex shrink-0 bg-blue-600 hover:bg-blue-700 text-white gap-2 text-xs"
              data-testid="collective-undertaking-mail-btn"
            >
              <Mail size={14} />
              {collectiveUndertakingLoading ? 'Sending...' : `Undertaking (${selectedOrders.size})`}
            </Button>
          )}
        </div>
      </div>

      {/* Mobile batch action bar — shown when orders are selected */}
      {isSuperAdminOrAdmin && selectedOrders.size > 0 && (
        <div className="sm:hidden flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg" data-testid="mobile-batch-bar">
          <span className="text-xs font-semibold text-blue-700 mr-1">{selectedOrders.size} selected</span>
          <button
            onClick={handleCollectiveCreditMail}
            disabled={collectiveCreditLoading}
            className="flex-1 py-2.5 text-xs bg-purple-600 text-white rounded-md font-semibold disabled:opacity-60"
            data-testid="collective-credit-mail-btn"
          >{collectiveCreditLoading ? '...' : 'Credit'}</button>
          <button
            onClick={handleCollectiveSaleOrderMail}
            disabled={collectiveSaleOrderLoading}
            className="flex-1 py-2.5 text-xs bg-emerald-600 text-white rounded-md font-semibold disabled:opacity-60"
            data-testid="collective-sale-order-mail-btn"
          >{collectiveSaleOrderLoading ? '...' : 'Sale Mail'}</button>
          <button
            onClick={handleCollectiveUndertakingMail}
            disabled={collectiveUndertakingLoading}
            className="flex-1 py-2.5 text-xs bg-blue-600 text-white rounded-md font-semibold disabled:opacity-60"
            data-testid="collective-undertaking-mail-btn"
          >{collectiveUndertakingLoading ? '...' : 'Undertaking'}</button>
          <button
            onClick={() => setSelectedOrders(new Set())}
            className="p-2 text-slate-500 hover:text-slate-700 rounded-md hover:bg-white"
          ><X size={16} /></button>
        </div>
      )}

      {/* Orders Table */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        {/* ── Desktop table (hidden on mobile) ───────────────────────────── */}
        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full erp-table" data-testid="order-table">
            <thead>
              <tr>
                {isSuperAdminOrAdmin && (
                  <th className="w-10 px-3">
                    <input
                      type="checkbox"
                      className="rounded border-slate-300 cursor-pointer"
                      checked={
                        orders.filter(o => o.status === 'SENT_TO_MILL').length > 0 &&
                        orders.filter(o => o.status === 'SENT_TO_MILL').every(o => selectedOrders.has(o.order_id))
                      }
                      onChange={toggleSelectAll}
                      title="Select all SENT_TO_MILL orders"
                      data-testid="select-all-checkbox"
                    />
                  </th>
                )}
                <th>Date</th>
                <th>DO Number</th>
                <th>Party</th>
                <th>Truck</th>
                <th>Sale Order</th>
                <th className="text-right">Qty (MT)</th>
                <th>Status</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={isSuperAdminOrAdmin ? 9 : 8} className="py-12 text-center text-slate-400">
                  <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
                </td></tr>
              ) : orders.length === 0 ? (
                <tr><td colSpan={isSuperAdminOrAdmin ? 9 : 8} className="py-12 text-center text-slate-400 text-sm">
                  No orders found.{' '}
                  <button onClick={() => navigate('/orders/new')} className="text-blue-600 hover:underline">
                    Create your first order
                  </button>
                </td></tr>
              ) : orders.map(order => {
                const date = order.created_at ? new Date(order.created_at).toLocaleDateString('en-IN') : '-';
                const totalQty = (order.items || []).reduce((s, i) => s + (i.quantity || 0), 0);
                const partyNames = [...new Set((order.items || []).map(i => i.bill_to_name).filter(Boolean))].join(', ') || '-';
                const saleOrder = order.sale_order_flag ?? order.sell_order_flag ?? true;
                const canCancelAsAdmin = isSuperAdminOrAdmin && order.status !== 'CANCELLED';
                const isBrokerOwned = user?.role === 'BROKER' && (order.created_by_user_id === user?.user_id || order.assigned_to_broker_id === user?.user_id);
                const canRequestDeletion = isBrokerOwned && !['CANCELLED'].includes(order.status);
                const isSentToMill = order.status === 'SENT_TO_MILL';
                const hasPayments = (order.party_payments || []).length > 0;
                const shortDO = shortenDO(order.do_number);
                return (
                  <tr key={order.order_id} data-testid={`order-row-${order.order_id}`}>
                    {isSuperAdminOrAdmin && (
                      <td className="text-center px-3">
                        {order.status === 'SENT_TO_MILL' && (
                          <input
                            type="checkbox"
                            className="rounded border-slate-300 cursor-pointer"
                            checked={selectedOrders.has(order.order_id)}
                            onChange={() => toggleOrderSelection(order.order_id)}
                            data-testid={`select-order-${order.order_id}`}
                          />
                        )}
                      </td>
                    )}
                    <td className="text-xs text-slate-500">{date}</td>
                    <td className="font-mono text-xs font-medium text-blue-700" title={order.do_number || ''}>
                      {shortDO || <span className="text-slate-400 italic">PENDING</span>}
                    </td>
                    <td className="font-medium text-slate-800 max-w-[150px] truncate" title={partyNames}>{partyNames}</td>
                    <td className="text-slate-600 text-xs">
                      {order.truck_no || <span className="italic text-slate-400">—</span>}
                    </td>
                    <td className="text-xs">
                      <div className="flex items-center gap-1 flex-wrap">
                        <span className={`px-1.5 py-0.5 rounded text-[11px] font-semibold ${saleOrder ? 'bg-blue-50 text-blue-700' : 'bg-orange-50 text-orange-700'}`}
                          data-testid={`sale-order-badge-${order.order_id}`}>
                          {saleOrder ? 'Yes' : 'No'}
                        </span>
                        {order.is_credit && (
                          <span className="px-1.5 py-0.5 rounded text-[11px] font-semibold bg-purple-50 text-purple-700"
                            data-testid={`credit-badge-${order.order_id}`}>Credit</span>
                        )}
                      </div>
                    </td>
                    <td className="text-right font-mono text-sm">{totalQty.toFixed(2)}</td>
                    <td><StatusBadge status={order.status} /></td>
                    <td>
                      <div className="flex items-center justify-end gap-1">
                        {isSuperAdminOrAdmin && ['PENDING', 'DRAFT'].includes(order.status) && (
                          <button
                            onClick={() => handleSendToMill(order.order_id)}
                            className="px-2 py-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded hover:bg-green-100 font-medium whitespace-nowrap"
                            data-testid={`finalize-btn-${order.order_id}`}
                            title="Send to Mill"
                          >Send to Mill</button>
                        )}
                        {isSuperAdminOrAdmin && isSentToMill && !order.is_credit && (
                          <button
                            onClick={() => handleCreditEmail(order.order_id)}
                            className="px-2 py-1 text-xs bg-purple-50 text-purple-700 border border-purple-200 rounded hover:bg-purple-100 font-medium whitespace-nowrap"
                            data-testid={`credit-email-btn-${order.order_id}`}
                            title="Mark as Credit & Send Email"
                          >Credit</button>
                        )}
                        {isSuperAdminOrAdmin && isSentToMill && hasPayments && (
                          <button
                            onClick={() => handlePaymentUndertaking(order.order_id)}
                            className="px-2 py-1 text-xs bg-teal-50 text-teal-700 border border-teal-200 rounded hover:bg-teal-100 font-medium whitespace-nowrap"
                            data-testid={`payment-undertaking-btn-${order.order_id}`}
                            title="Send Payment Undertaking Email"
                          >Pay. Undertaking</button>
                        )}
                        {order.status !== 'CANCELLED' && (
                          <button
                            onClick={() => navigate(`/orders/${order.order_id}/edit`)}
                            className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                            data-testid={`edit-btn-${order.order_id}`}
                            title="Edit"
                          ><Pencil size={14} /></button>
                        )}
                        {order.do_number && (
                          <button
                            onClick={() => handleDownloadPdf(order.order_id, order.do_number)}
                            disabled={pdfLoading}
                            className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded disabled:opacity-50"
                            data-testid={`pdf-btn-${order.order_id}`}
                            title="View / Download PDF"
                          >
                            {pdfLoading ? (
                              <div className="w-3.5 h-3.5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <FileText size={14} />
                            )}
                          </button>
                        )}
                        {isSuperAdminOrAdmin && (
                          <button
                            onClick={() => handleViewHistory(order.order_id, order.do_number)}
                            className="p-1.5 text-slate-400 hover:text-violet-600 hover:bg-violet-50 rounded"
                            data-testid={`history-btn-${order.order_id}`}
                            title="View History"
                          ><Clock size={14} /></button>
                        )}
                        {(canCancelAsAdmin) && (
                          <button
                            onClick={() => handleCancel(order.order_id)}
                            className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded"
                            data-testid={`cancel-btn-${order.order_id}`}
                            title="Cancel"
                          ><XCircle size={14} /></button>
                        )}
                        {canRequestDeletion && (
                          <button
                            onClick={() => setDeletionModal({ order_id: order.order_id, do_number: order.do_number })}
                            className="p-1.5 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded"
                            data-testid={`req-del-btn-${order.order_id}`}
                            title="Request Deletion"
                          ><XCircle size={14} /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── Mobile card list (hidden on sm+) ───────────────────────────── */}
        <div className="sm:hidden" data-testid="order-cards-mobile">
          {loading ? (
            <div className="py-12 flex justify-center">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : orders.length === 0 ? (
            <div className="py-12 text-center text-slate-400 text-sm px-4">
              No orders found.{' '}
              <button onClick={() => navigate('/orders/new')} className="text-blue-600 hover:underline">
                Create your first order
              </button>
            </div>
          ) : orders.map(order => {
            const date = order.created_at ? new Date(order.created_at).toLocaleDateString('en-IN') : '-';
            const totalQty = (order.items || []).reduce((s, i) => s + (i.quantity || 0), 0);
            const partyNames = [...new Set((order.items || []).map(i => i.bill_to_name).filter(Boolean))].join(', ') || '-';
            const saleOrder = order.sale_order_flag ?? order.sell_order_flag ?? true;
            const canCancelAsAdmin = isSuperAdminOrAdmin && order.status !== 'CANCELLED';
            const isBrokerOwned = user?.role === 'BROKER' && (order.created_by_user_id === user?.user_id || order.assigned_to_broker_id === user?.user_id);
            const canRequestDeletion = isBrokerOwned && !['CANCELLED'].includes(order.status);
            const isSentToMill = order.status === 'SENT_TO_MILL';
            const hasPayments = (order.party_payments || []).length > 0;
            const shortDO = shortenDO(order.do_number);
            return (
              <div key={order.order_id}
                className="border-b border-slate-100 last:border-0 px-4 py-3.5"
                data-testid={`order-card-${order.order_id}`}>
                {/* Card header: checkbox + DO# + status */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2.5 min-w-0">
                    {isSuperAdminOrAdmin && order.status === 'SENT_TO_MILL' && (
                      <input
                        type="checkbox"
                        className="rounded border-slate-300 cursor-pointer w-4 h-4 shrink-0 mt-0.5"
                        checked={selectedOrders.has(order.order_id)}
                        onChange={() => toggleOrderSelection(order.order_id)}
                        data-testid={`select-order-${order.order_id}`}
                      />
                    )}
                    <div className="min-w-0">
                      <div className="font-mono text-sm font-semibold text-blue-700 leading-tight">
                        {shortDO || <span className="text-slate-400 italic font-sans text-xs">No DO#</span>}
                      </div>
                      <div className="text-xs text-slate-400 mt-0.5">{date}</div>
                    </div>
                  </div>
                  <StatusBadge status={order.status} />
                </div>

                {/* Party name */}
                <div className="text-sm font-medium text-slate-800 mb-1.5 truncate">{partyNames}</div>

                {/* Meta row: truck + qty + badges */}
                <div className="flex items-center gap-2 flex-wrap mb-3">
                  {order.truck_no && (
                    <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded font-mono">{order.truck_no}</span>
                  )}
                  <span className="text-xs font-semibold text-slate-700">{totalQty.toFixed(2)} MT</span>
                  <span className={`text-[11px] font-semibold px-1.5 py-0.5 rounded ${saleOrder ? 'bg-blue-50 text-blue-700' : 'bg-orange-50 text-orange-700'}`}>
                    {saleOrder ? 'Sale' : 'Truck'}
                  </span>
                  {order.is_credit && (
                    <span className="text-[11px] font-semibold px-1.5 py-0.5 rounded bg-purple-50 text-purple-700">Credit</span>
                  )}
                </div>

                {/* Action buttons */}
                <div className="flex items-center gap-2 flex-wrap">
                  {isSuperAdminOrAdmin && ['PENDING', 'DRAFT'].includes(order.status) && (
                    <button
                      onClick={() => handleSendToMill(order.order_id)}
                      className="flex-1 min-w-0 py-2 px-3 text-xs bg-green-50 text-green-700 border border-green-200 rounded-md font-semibold text-center"
                      data-testid={`finalize-btn-${order.order_id}`}
                    >Send to Mill</button>
                  )}
                  {isSuperAdminOrAdmin && isSentToMill && !order.is_credit && (
                    <button
                      onClick={() => handleCreditEmail(order.order_id)}
                      className="py-2 px-3 text-xs bg-purple-50 text-purple-700 border border-purple-200 rounded-md font-semibold"
                      data-testid={`credit-email-btn-${order.order_id}`}
                    >Credit</button>
                  )}
                  {isSuperAdminOrAdmin && isSentToMill && hasPayments && (
                    <button
                      onClick={() => handlePaymentUndertaking(order.order_id)}
                      className="py-2 px-3 text-xs bg-teal-50 text-teal-700 border border-teal-200 rounded-md font-semibold"
                      data-testid={`payment-undertaking-btn-${order.order_id}`}
                    >Undertaking</button>
                  )}
                  {order.status !== 'CANCELLED' && (
                    <button
                      onClick={() => navigate(`/orders/${order.order_id}/edit`)}
                      className="p-2.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md border border-slate-200"
                      data-testid={`edit-btn-${order.order_id}`}
                      title="Edit"
                    ><Pencil size={15} /></button>
                  )}
                  {order.do_number && (
                    <button
                      onClick={() => handleDownloadPdf(order.order_id, order.do_number)}
                      disabled={pdfLoading}
                      className="p-2.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md border border-slate-200 disabled:opacity-50"
                      data-testid={`pdf-btn-${order.order_id}`}
                      title="View PDF"
                    ><FileText size={15} /></button>
                  )}
                  {isSuperAdminOrAdmin && (
                    <button
                      onClick={() => handleViewHistory(order.order_id, order.do_number)}
                      className="p-2.5 text-slate-400 hover:text-violet-600 hover:bg-violet-50 rounded-md border border-slate-200"
                      data-testid={`history-btn-${order.order_id}`}
                      title="History"
                    ><Clock size={15} /></button>
                  )}
                  {canCancelAsAdmin && (
                    <button
                      onClick={() => handleCancel(order.order_id)}
                      className="p-2.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md border border-slate-200"
                      data-testid={`cancel-btn-${order.order_id}`}
                      title="Cancel"
                    ><XCircle size={15} /></button>
                  )}
                  {canRequestDeletion && (
                    <button
                      onClick={() => setDeletionModal({ order_id: order.order_id, do_number: order.do_number })}
                      className="p-2.5 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded-md border border-slate-200"
                      data-testid={`req-del-btn-${order.order_id}`}
                      title="Request Deletion"
                    ><XCircle size={15} /></button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {total > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
            <span className="text-xs text-slate-500">{total} total orders</span>
            <div className="flex gap-2">
              <button disabled={page === 0} onClick={() => setPage(p => p - 1)} className="px-3 py-1.5 text-xs border rounded disabled:opacity-40 hover:bg-slate-50">Prev</button>
              <span className="px-3 py-1.5 text-xs text-slate-600">Page {page + 1}</span>
              <button disabled={(page + 1) * PAGE_SIZE >= total} onClick={() => setPage(p => p + 1)} className="px-3 py-1.5 text-xs border rounded disabled:opacity-40 hover:bg-slate-50">Next</button>
            </div>
          </div>
        )}
      </div>

      {/* PDF Viewer Modal */}
      {pdfViewer && (
        <div className="fixed inset-0 z-50 flex flex-col bg-gray-900" data-testid="pdf-viewer-modal">
          <div className="flex items-center justify-between px-4 py-2.5 bg-white border-b border-gray-200 shadow-sm shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-7 h-7 bg-blue-600 rounded flex items-center justify-center">
                <FileText size={14} className="text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800 leading-tight">{pdfViewer.name}</p>
                <p className="text-xs text-gray-400">Delivery Order</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  // Print only the PDF, not the whole screen
                  const iframe = document.createElement('iframe');
                  iframe.style.cssText = 'position:fixed;top:0;left:0;width:0;height:0;opacity:0;border:none';
                  iframe.src = pdfViewer.url;
                  document.body.appendChild(iframe);
                  iframe.addEventListener('load', () => {
                    try { iframe.contentWindow.print(); } catch { window.open(pdfViewer.url, '_blank'); }
                    setTimeout(() => document.body.removeChild(iframe), 2000);
                  });
                }}
                className="flex items-center gap-1.5 px-3 py-1.5 border border-slate-300 text-slate-700 text-xs font-semibold rounded-md hover:bg-slate-50"
                data-testid="pdf-print-btn"
              >
                <Printer size={12} /> Print
              </button>
              <a
                href={pdfViewer.url}
                download={pdfViewer.name}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-md hover:bg-blue-700"
                data-testid="pdf-download-btn"
              >
                <Download size={12} /> Download PDF
              </a>
              <button
                onClick={closePdfViewer}
                className="p-1.5 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded"
                data-testid="pdf-close-btn"
              ><X size={16} /></button>
            </div>
          </div>
          <embed
            src={pdfViewer.url}
            type="application/pdf"
            className="flex-1 w-full border-0"
            style={{ minHeight: 0, height: 'calc(100vh - 48px)' }}
          />
        </div>
      )}

      {/* Order History Modal */}
      {historyModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={e => e.target === e.currentTarget && setHistoryModal(null)}
          data-testid="history-modal"
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 shrink-0">
              <div className="flex items-center gap-2">
                <Clock size={15} className="text-slate-500" />
                <h3 className="text-sm font-semibold text-slate-800">Order History</h3>
                <span className="text-xs text-slate-400 font-mono">{historyModal.displayId}</span>
              </div>
              <button onClick={() => setHistoryModal(null)} className="text-slate-400 hover:text-slate-700">
                <X size={16} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {historyModal.loading ? (
                <div className="flex justify-center py-8">
                  <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : !historyModal.data?.length ? (
                <p className="text-sm text-slate-400 text-center py-8">No history records found for this order.</p>
              ) : historyModal.data.map((h) => (
                <div key={h.history_id} className="border border-slate-200 rounded-lg overflow-hidden">
                  <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${h.action === 'CANCEL' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}`}>
                        {h.action}
                      </span>
                      <span className="text-xs text-slate-500">
                        {h.changed_at ? new Date(h.changed_at).toLocaleString('en-IN') : ''}
                      </span>
                    </div>
                    <span className="text-xs text-slate-400 font-mono">{h.changed_by_user_id}</span>
                  </div>
                  <pre className="p-3 text-xs text-slate-600 overflow-x-auto bg-white max-h-56 overflow-y-auto leading-relaxed">
                    {JSON.stringify(h.snapshot, null, 2)}
                  </pre>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Deletion Request Modal (Broker submits) */}
      {deletionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-sm">
            <div className="px-5 py-4 border-b border-slate-200">
              <h3 className="text-sm font-semibold text-slate-800">Request Order Deletion</h3>
              <p className="text-xs text-slate-500 mt-0.5">DO: {shortenDO(deletionModal.do_number) || deletionModal.order_id}</p>
            </div>
            <div className="px-5 py-4 space-y-3">
              <p className="text-sm text-slate-600">
                You are requesting that an Admin cancel this order. Please provide a reason:
              </p>
              <textarea
                value={deletionReason}
                onChange={e => setDeletionReason(e.target.value)}
                placeholder="Reason for deletion..."
                rows={3}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 resize-none"
                data-testid="deletion-reason-input"
              />
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-slate-100">
              <button
                onClick={() => { setDeletionModal(null); setDeletionReason(''); }}
                className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-800 border border-slate-200 rounded"
              >Cancel</button>
              <button
                onClick={handleSubmitDeletionRequest}
                disabled={deletionSubmitting}
                className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 text-white rounded font-medium"
                data-testid="confirm-deletion-request-btn"
              >{deletionSubmitting ? 'Submitting...' : 'Submit Request'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
